import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";
import MenuItem from "./MenuItem";
import Option from "./Option";
import Popover from "./Popover";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Converte filhos <MenuItem> em <Option> para funcionar dentro do <select> nativo.
// Quando displayEmpty=false (default), filtra opções cujo value é vazio/undefined.
const normalizeOptions = (children: React.ReactNode, displayEmpty: boolean): React.ReactNode =>
  React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (child.type === MenuItem) {
      const { value, disabled, children: content } = child.props as {
        value?: string | number | readonly string[];
        disabled?: boolean;
        children?: React.ReactNode;
      };

      const isEmpty = value === undefined || value === "" || value === null;

      if (isEmpty && !displayEmpty) return null;

      return (
        <Option value={value ?? ""} disabled={disabled}>
          {content}
        </Option>
      );
    }

    return child;
  });

// Compara o valor selecionado com o value de cada opção. Suporta objetos:
// se ambos tiverem `id`, compara por id; caso contrário, usa igualdade estrita
// ou comparação estrutural via JSON.
const isSameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a && b && typeof a === "object" && typeof b === "object") {
    const aId = (a as { id?: unknown }).id;
    const bId = (b as { id?: unknown }).id;

    if (aId !== undefined || bId !== undefined) return aId === bId;

    return JSON.stringify(a) === JSON.stringify(b);
  }

  return false;
};

// Ícone de seta padrão do MUI
const DefaultIcon = () => (
  <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

type SelectValue = React.SelectHTMLAttributes<HTMLSelectElement>["value"];

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  variant?: "outlined" | "standard" | "filled";
  color?: "primary" | "secondary";
  IconComponent?: React.ElementType;
  InputLabelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  /**
   * Quando false (default), opções com value vazio são omitidas — assim a
   * primeira opção não-vazia aparece como selecionada por padrão. Quando true,
   * mantém a opção de valor vazio (útil como placeholder).
   */
  displayEmpty?: boolean;
  /**
   * Customiza o texto exibido no controle a partir do valor selecionado.
   * Equivalente ao `renderValue` do Select do MUI. Usado apenas no modo `multiple`.
   */
  renderValue?: (value: SelectValue) => React.ReactNode;
  /** Aceito por compatibilidade com o MUI; ignorado no dropdown customizado. */
  MenuProps?: unknown;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const {
    children,
    label,
    helperText,
    error = false,
    fullWidth = false,
    // variant = "outlined",
    color = "primary",
    className,
    id,
    disabled,
    required,
    IconComponent = DefaultIcon,
    InputLabelProps,
    displayEmpty = false,
    multiple = false,
    renderValue,
    // MenuProps,
    value,
    name,
    onChange,
    style,
    ...other
  } = props;

  // Lógica de cores baseada no estado (Foco e Erro)
  const getBorderColor = () => {
    if (error) return "border-red-600 focus:border-red-600 focus:ring-red-600";
    const focusColor = color === "primary" ? "focus:border-primary focus:ring-primary" : "focus:border-secondary focus:ring-secondary";

    return `border-slate-300 hover:border-slate-900 ${focusColor}`;
  };

  const getLabelColor = () => {
    if (error) return "text-red-600";
    if (disabled) return "text-slate-400";

    return "text-slate-500 group-focus-within:text-primary"; // Simplificado
  };

  // --- Modo seleção múltipla (dropdown customizado) ---
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const selectedValues: string[] = Array.isArray(value) ? (value as string[]) : [];

  const isChecked = (optionValue: unknown) =>
    selectedValues.some((v) => String(v) === String(optionValue));

  const toggleValue = (optionValue: unknown) => {
    const next = isChecked(optionValue)
      ? selectedValues.filter((v) => String(v) !== String(optionValue))
      : [...selectedValues, String(optionValue)];

    // Evento sintético no formato do MUI ({ target: { value, name } }), aceito
    // tanto pelos handlers existentes quanto pelo Controller do react-hook-form.
    onChange?.({ target: { value: next, name } } as unknown as React.ChangeEvent<HTMLSelectElement>);
  };

  const display: React.ReactNode = renderValue
    ? renderValue(value)
    : selectedValues.join(", ");

  const controlClassName = cn(
    "appearance-none w-full px-3 py-2.5 min-h-[2.75rem] bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
    "text-slate-900 text-left cursor-pointer focus:ring-1",
    getBorderColor(),
    disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
  );

  // Renderiza os <MenuItem> como linhas clicáveis (em vez de <option> nativo),
  // permitindo Checkbox/ListItemText dentro de cada item.
  const renderMultipleItems = () =>
    React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      if (child.type !== MenuItem) return child;

      const { value: optionValue, disabled: optionDisabled, children: content } =
        child.props as { value?: unknown; disabled?: boolean; children?: React.ReactNode };

      return (
        <li
          role="option"
          aria-selected={isChecked(optionValue)}
          className={cn(
            "flex items-center px-2 py-1 text-sm transition-colors",
            optionDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-100"
          )}
          onClick={() => !optionDisabled && toggleValue(optionValue)}
        >
          {content}
        </li>
      );
    });

  // --- Modo seleção única com valores em objeto ---
  // O <select> nativo só lida com strings, então mapeamos cada <MenuItem> cujo
  // value é um objeto para uma chave string (índice) e, no onChange, devolvemos
  // o objeto original em e.target.value.
  const childrenArray = React.Children.toArray(children);
  const hasObjectValue = childrenArray.some(
    (child) =>
      React.isValidElement(child) &&
      child.type === MenuItem &&
      typeof (child.props as { value?: unknown }).value === "object" &&
      (child.props as { value?: unknown }).value !== null
  );

  const isOptionChild = (child: React.ReactNode): child is React.ReactElement =>
    React.isValidElement(child) &&
    (child.type === MenuItem || child.type === Option || child.type === "option");

  const hasSelectedOption = childrenArray.some((child) => {
    if (!isOptionChild(child)) return false;

    const optionValue = (child.props as { value?: unknown }).value;
    const isEmpty = optionValue === undefined || optionValue === "" || optionValue === null;

    if (child.type === MenuItem && isEmpty && !displayEmpty) return false;

    return String(optionValue ?? "") === String(value ?? "");
  });

  if (!multiple && hasObjectValue) {
    const objectOptions = childrenArray
      .filter(
        (child): child is React.ReactElement =>
          React.isValidElement(child) && child.type === MenuItem
      )
      .map((child, index) => {
        const { value: optionValue, disabled: optionDisabled, children: content } =
          child.props as { value?: unknown; disabled?: boolean; children?: React.ReactNode };

        return { key: String(index), value: optionValue, disabled: optionDisabled, content };
      });

    const selected = objectOptions.find((option) => isSameValue(option.value, value));

    const handleObjectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const option = objectOptions.find((opt) => opt.key === event.target.value);

      onChange?.({ target: { value: option ? option.value : "", name } } as unknown as React.ChangeEvent<HTMLSelectElement>);
    };

    return (
      <div className={cn("inline-flex flex-col relative group", fullWidth && "w-full", className)}>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "block text-sm font-medium mb-1.5 ml-1 transition-colors duration-200",
              getLabelColor()
            )}
            {...InputLabelProps}
          >
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={id}
            name={name}
            disabled={disabled}
            required={required}
            value={selected ? selected.key : ""}
            style={style}
            onChange={handleObjectChange}
            className={cn(
              "appearance-none w-full px-3 py-2.5 bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
              "text-slate-900 cursor-pointer focus:ring-1",
              getBorderColor(),
              disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
            )}
            {...other}
          >
            {!selected && <option value="" disabled hidden />}
            {objectOptions.map((option) => (
              <option key={option.key} value={option.key} disabled={option.disabled}>
                {option.content}
              </option>
            ))}
          </select>

          <div className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200",
            disabled ? "text-slate-300" : "text-slate-500"
          )}>
            <IconComponent />
          </div>
        </div>

        {helperText && (
          <p className={cn(
            "mt-1.5 text-xs ml-3.5",
            error ? "text-red-600" : "text-slate-500"
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }

  if (multiple) {
    return (
      <div
        className={cn("inline-flex flex-col relative group", fullWidth && "w-full", className)}
        style={style}
      >
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "block text-sm font-medium mb-1.5 ml-1 transition-colors duration-200",
              getLabelColor()
            )}
            {...InputLabelProps}
          >
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}

        <div className="relative">
          <button
            type="button"
            ref={anchorRef}
            id={id}
            disabled={disabled}
            className={controlClassName}
            onClick={() => !disabled && setOpen((prev) => !prev)}
          >
            <span className="block truncate pr-6">{display}</span>
          </button>

          <div className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200",
            disabled ? "text-slate-300" : "text-slate-500"
          )}>
            <IconComponent />
          </div>
        </div>

        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            className: "max-h-72 overflow-auto border border-slate-200",
            style: { width: anchorRef.current?.offsetWidth },
          }}
        >
          <ul className="py-1">{renderMultipleItems()}</ul>
        </Popover>

        {helperText && (
          <p className={cn(
            "mt-1.5 text-xs ml-3.5",
            error ? "text-red-600" : "text-slate-500"
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn("inline-flex flex-col relative group", fullWidth && "w-full", className)}>
      {/* Label superior fixo (estilo TextField anterior) */}
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium mb-1.5 ml-1 transition-colors duration-200",
            getLabelColor()
          )}
          {...InputLabelProps}
        >
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          ref={ref}
          id={id}
          name={name}
          disabled={disabled}
          required={required}
          value={value}
          style={style}
          onChange={onChange}
          className={cn(
            "appearance-none w-full px-3 py-2.5 bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
            "text-slate-900 cursor-pointer focus:ring-1",
            getBorderColor(),
            disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
          )}
          {...other}
        >
          {!hasSelectedOption && <option value={String(value ?? "")} disabled hidden />}
          {normalizeOptions(children, displayEmpty)}
        </select>

        {/* Ícone de Seta customizado posicionado de forma absoluta */}
        <div className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200",
          disabled ? "text-slate-300" : "text-slate-500"
        )}>
          <IconComponent />
        </div>
      </div>

      {/* Helper Text */}
      {helperText && (
        <p className={cn(
          "mt-1.5 text-xs ml-3.5",
          error ? "text-red-600" : "text-slate-500"
        )}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = "Select";

export default Select;